import { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../contexts/AuthContext';
import { appsApi, App } from '../services/api';
import styles from './Apps.module.css';

const STATUS_COLORS: Record<App['status'], string> = {
  published: '#22c55e',
  on_deck: '#f59e0b',
  gestating: '#3b82f6',
  archived: '#6b7280',
};

const OWNER_LABELS: Record<App['revenueOwner'], string> = {
  charsoft: 'Charsoft (100%)',
  csl: 'CSL Leesburg',
  partnership: 'Partnership',
};

export const APP_CATEGORIES: Record<string, { label: string; icon: string }> = {
  csl_course_management: { label: 'CSL Course Management', icon: '📚' },
  csl_ministry: { label: 'CSL & Ministry', icon: '🌿' },
  cci_education: { label: 'CTE & Education (CCI Connect)', icon: '🎓' },
  media_content: { label: 'Media & Content Creation', icon: '🎬' },
  music_audio: { label: 'Music & Audio', icon: '🎵' },
  mobile_practice: { label: 'Mobile & Daily Practice', icon: '📱' },
  internal_tools: { label: 'Internal Tools & Automation', icon: '🛠️' },
  other: { label: 'Other / Misc', icon: '📁' },
};

export function getAppCategoryKey(app: App): string {
  if (app.category && APP_CATEGORIES[app.category]) {
    return app.category;
  }

  // Automatic smart categorization fallback
  const text = `${app.name} ${app.slug} ${app.description || ''} ${app.gcpProjectId || ''}`.toLowerCase();

  if (text.includes('course') || text.includes('mystical-path') || text.includes('mystic path') || text.includes('student planner') || text.includes('homework') || text.includes('curriculum')) {
    return 'csl_course_management';
  }
  if (text.includes('open-at-the-top') || text.includes('open at the top') || text.includes('conscious-giving') || text.includes('giving') || text.includes('csl-shop') || text.includes('sunday-service') || text.includes('sunday service') || text.includes('oracle') || (app.revenueOwner === 'csl' && !text.includes('course'))) {
    return 'csl_ministry';
  }
  if (text.includes('cci') || text.includes('cte') || text.includes('ngss') || text.includes('lesson')) {
    return 'cci_education';
  }
  if (text.includes('song') || text.includes('choir') || text.includes('singable') || text.includes('satb') || text.includes('music')) {
    return 'music_audio';
  }
  if (text.includes('channel') || text.includes('vision') || text.includes('veo') || text.includes('podcast') || text.includes('wisdom') || text.includes('beguine') || text.includes('solfeggio') || text.includes('thumbnail') || text.includes('slicer')) {
    return 'media_content';
  }
  if (text.includes('affirmation') || text.includes('admob') || text.includes('unbelizeable') || text.includes('mobile')) {
    return 'mobile_practice';
  }
  return 'other';
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/`/g, '&#96;');
}

export default function Apps() {
  const { getIdToken } = useAuth();
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  useEffect(() => {
    loadApps();
  }, [statusFilter]);

  async function loadApps() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) throw new Error('Not authenticated');

      const filters = statusFilter ? { status: statusFilter } : undefined;
      const response = await appsApi.list(token, filters);
      setApps(response.apps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  }

  async function handleEditApp(app: App) {
    const activeCategory = getAppCategoryKey(app);

    const categoryOptionsHtml = Object.entries(APP_CATEGORIES)
      .map(([key, cat]) => `<option value="${key}" ${activeCategory === key ? 'selected' : ''}>${cat.icon} ${cat.label}</option>`)
      .join('');

    const result = await Swal.fire({
      title: 'Edit App',
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500;">App Name *</label>
          <input id="swal-name" class="swal2-input" value="${escapeHtml(app.name)}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Category</label>
          <select id="swal-category" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            ${categoryOptionsHtml}
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Description</label>
          <textarea id="swal-description" class="swal2-textarea" style="width: 100%; margin: 0 0 12px 0;">${escapeHtml(app.description || '')}</textarea>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Status</label>
          <select id="swal-status" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="gestating" ${app.status === 'gestating' ? 'selected' : ''}>Gestating</option>
            <option value="on_deck" ${app.status === 'on_deck' ? 'selected' : ''}>On Deck</option>
            <option value="published" ${app.status === 'published' ? 'selected' : ''}>Published</option>
            <option value="archived" ${app.status === 'archived' ? 'selected' : ''}>Archived</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Revenue Owner</label>
          <select id="swal-owner" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="charsoft" ${app.revenueOwner === 'charsoft' ? 'selected' : ''}>Charsoft (100%)</option>
            <option value="csl" ${app.revenueOwner === 'csl' ? 'selected' : ''}>CSL Leesburg</option>
            <option value="partnership" ${app.revenueOwner === 'partnership' ? 'selected' : ''}>Partnership</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Monetization</label>
          <select id="swal-monetization" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="none" ${app.monetization.type === 'none' ? 'selected' : ''}>None</option>
            <option value="stripe_subscription" ${app.monetization.type === 'stripe_subscription' ? 'selected' : ''}>Stripe Subscription</option>
            <option value="stripe_onetime" ${app.monetization.type === 'stripe_onetime' ? 'selected' : ''}>Stripe One-time</option>
            <option value="admob" ${app.monetization.type === 'admob' ? 'selected' : ''}>AdMob</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Tech Stack (comma-separated)</label>
          <input id="swal-tech" class="swal2-input" value="${escapeHtml(app.techStack.join(', '))}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">GCP Project ID</label>
          <input id="swal-gcp" class="swal2-input" value="${escapeHtml(app.gcpProjectId || '')}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Live URL</label>
          <input id="swal-url" class="swal2-input" value="${escapeHtml(app.liveUrl || '')}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">GitHub Repo</label>
          <input id="swal-github" class="swal2-input" value="${escapeHtml(app.githubRepo || '')}" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Notes</label>
          <textarea id="swal-notes" class="swal2-textarea" placeholder="Deploy steps, testing commands, reminders..." style="width: 100%; margin: 0; min-height: 100px;">${escapeHtml(app.notes || '')}</textarea>
        </div>
      `,
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: 'Save Changes',
      denyButtonText: 'Delete',
      confirmButtonColor: '#3b82f6',
      denyButtonColor: '#ef4444',
      preConfirm: () => {
        const name = (document.getElementById('swal-name') as HTMLInputElement).value;
        if (!name.trim()) {
          Swal.showValidationMessage('App name is required');
          return false;
        }
        return {
          name: name.trim(),
          category: (document.getElementById('swal-category') as HTMLSelectElement).value,
          description: (document.getElementById('swal-description') as HTMLTextAreaElement).value.trim(),
          status: (document.getElementById('swal-status') as HTMLSelectElement).value as App['status'],
          revenueOwner: (document.getElementById('swal-owner') as HTMLSelectElement).value as App['revenueOwner'],
          monetization: {
            type: (document.getElementById('swal-monetization') as HTMLSelectElement).value as App['monetization']['type'],
            stripeProductIds: app.monetization.stripeProductIds,
            admobAppId: app.monetization.admobAppId,
          },
          techStack: (document.getElementById('swal-tech') as HTMLInputElement).value
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
          gcpProjectId: (document.getElementById('swal-gcp') as HTMLInputElement).value.trim() || null,
          liveUrl: (document.getElementById('swal-url') as HTMLInputElement).value.trim() || null,
          githubRepo: (document.getElementById('swal-github') as HTMLInputElement).value.trim() || null,
          notes: (document.getElementById('swal-notes') as HTMLTextAreaElement).value.trim() || null,
        };
      },
    });

    if (result.isDenied) {
      handleDeleteApp(app);
      return;
    }

    if (result.value) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await appsApi.update(token, app.id, result.value);

        await Swal.fire({
          icon: 'success',
          title: 'App Updated!',
          timer: 1500,
          showConfirmButton: false,
        });

        loadApps();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to update app',
        });
      }
    }
  }

  async function handleDeleteApp(app: App) {
    const result = await Swal.fire({
      title: 'Delete App?',
      text: `Are you sure you want to delete "${app.name}"? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      confirmButtonColor: '#ef4444',
    });

    if (result.isConfirmed) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await appsApi.delete(token, app.id, true);

        await Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: `${app.name} has been removed.`,
          timer: 1500,
          showConfirmButton: false,
        });

        loadApps();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to delete app',
        });
      }
    }
  }

  async function handleAddApp() {
    const categoryOptionsHtml = Object.entries(APP_CATEGORIES)
      .map(([key, cat]) => `<option value="${key}">${cat.icon} ${cat.label}</option>`)
      .join('');

    const { value: formValues } = await Swal.fire({
      title: 'Add New App',
      html: `
        <div style="text-align: left;">
          <label style="display: block; margin-bottom: 4px; font-weight: 500;">App Name *</label>
          <input id="swal-name" class="swal2-input" placeholder="My Awesome App" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Category</label>
          <select id="swal-category" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            ${categoryOptionsHtml}
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Description</label>
          <textarea id="swal-description" class="swal2-textarea" placeholder="Brief description of the app" style="width: 100%; margin: 0 0 12px 0;"></textarea>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Status</label>
          <select id="swal-status" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="gestating">Gestating</option>
            <option value="on_deck">On Deck</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Revenue Owner</label>
          <select id="swal-owner" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="charsoft">Charsoft (100%)</option>
            <option value="csl">CSL Leesburg</option>
            <option value="partnership">Partnership</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Monetization</label>
          <select id="swal-monetization" class="swal2-select" style="width: 100%; margin: 0 0 12px 0;">
            <option value="none">None</option>
            <option value="stripe_subscription">Stripe Subscription</option>
            <option value="stripe_onetime">Stripe One-time</option>
            <option value="admob">AdMob</option>
          </select>

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Tech Stack (comma-separated)</label>
          <input id="swal-tech" class="swal2-input" placeholder="React, Node.js, Firebase" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">GCP Project ID</label>
          <input id="swal-gcp" class="swal2-input" placeholder="my-gcp-project" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Live URL</label>
          <input id="swal-url" class="swal2-input" placeholder="https://myapp.com" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">GitHub Repo</label>
          <input id="swal-github" class="swal2-input" placeholder="username/repo" style="width: 100%; margin: 0 0 12px 0;">

          <label style="display: block; margin-bottom: 4px; font-weight: 500;">Notes</label>
          <textarea id="swal-notes" class="swal2-textarea" placeholder="Deploy steps, testing commands, reminders..." style="width: 100%; margin: 0; min-height: 100px;"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Create App',
      confirmButtonColor: '#3b82f6',
      preConfirm: () => {
        const name = (document.getElementById('swal-name') as HTMLInputElement).value;
        if (!name.trim()) {
          Swal.showValidationMessage('App name is required');
          return false;
        }
        const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        return {
          name: name.trim(),
          slug,
          category: (document.getElementById('swal-category') as HTMLSelectElement).value,
          description: (document.getElementById('swal-description') as HTMLTextAreaElement).value.trim(),
          status: (document.getElementById('swal-status') as HTMLSelectElement).value as App['status'],
          revenueOwner: (document.getElementById('swal-owner') as HTMLSelectElement).value as App['revenueOwner'],
          partnershipId: null,
          monetization: {
            type: (document.getElementById('swal-monetization') as HTMLSelectElement).value as App['monetization']['type'],
            stripeProductIds: [],
            admobAppId: null,
          },
          techStack: (document.getElementById('swal-tech') as HTMLInputElement).value
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
          gcpProjectId: (document.getElementById('swal-gcp') as HTMLInputElement).value.trim() || null,
          liveUrl: (document.getElementById('swal-url') as HTMLInputElement).value.trim() || null,
          githubRepo: (document.getElementById('swal-github') as HTMLInputElement).value.trim() || null,
          notes: (document.getElementById('swal-notes') as HTMLTextAreaElement).value.trim() || null,
          thumbnail: null,
          launchedAt: null,
        };
      },
    });

    if (formValues) {
      try {
        const token = await getIdToken();
        if (!token) throw new Error('Not authenticated');

        await appsApi.create(token, formValues);

        await Swal.fire({
          icon: 'success',
          title: 'App Created!',
          text: `${formValues.name} has been added to your portfolio.`,
          timer: 2000,
          showConfirmButton: false,
        });

        loadApps();
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to create app',
        });
      }
    }
  }

  if (loading) {
    return <div className={styles.container}><p>Loading apps...</p></div>;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.error}>{error}</p>
        <button onClick={loadApps} className="btn btn-primary">Retry</button>
      </div>
    );
  }

  // Filter apps by category if selected
  const filteredApps = apps.filter((app) => {
    if (categoryFilter) {
      const catKey = getAppCategoryKey(app);
      if (catKey !== categoryFilter) return false;
    }
    return true;
  });

  // Group apps by resolved category key
  const groupedApps: Record<string, App[]> = {};
  filteredApps.forEach((app) => {
    const key = getAppCategoryKey(app);
    if (!groupedApps[key]) {
      groupedApps[key] = [];
    }
    groupedApps[key].push(app);
  });

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>App Portfolio</h1>
        <div className={styles.actions}>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className={styles.filter}
          >
            <option value="">All categories</option>
            {Object.entries(APP_CATEGORIES).map(([key, cat]) => (
              <option key={key} value={key}>
                {cat.icon} {cat.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={styles.filter}
          >
            <option value="">All statuses</option>
            <option value="published">Published</option>
            <option value="on_deck">On Deck</option>
            <option value="gestating">Gestating</option>
            <option value="archived">Archived</option>
          </select>

          <button className="btn btn-primary" onClick={handleAddApp}>+ Add App</button>
        </div>
      </div>

      {filteredApps.length === 0 ? (
        <p className={styles.empty}>No apps found matching your filters.</p>
      ) : (
        Object.entries(APP_CATEGORIES).map(([catKey, catInfo]) => {
          const categoryApps = groupedApps[catKey];
          if (!categoryApps || categoryApps.length === 0) return null;

          return (
            <div key={catKey} className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <span className={styles.categoryIcon}>{catInfo.icon}</span>
                <h2 className={styles.categoryTitle}>{catInfo.label}</h2>
                <span className={styles.categoryCount}>{categoryApps.length}</span>
              </div>

              <div className={styles.grid}>
                {categoryApps.map((app) => {
                  return (
                    <div
                      key={app.id}
                      className={styles.card}
                      onClick={() => handleEditApp(app)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className={styles.categoryBadge}>
                        <span>{catInfo.icon} {catInfo.label}</span>
                      </div>

                      <div className={styles.cardHeader}>
                        <h3 className={styles.appName}>{app.name}</h3>
                        <span
                          className={styles.status}
                          style={{ backgroundColor: STATUS_COLORS[app.status] }}
                        >
                          {app.status.replace('_', ' ')}
                        </span>
                      </div>
                      <p className={styles.description}>{app.description || 'No description'}</p>
                      <div className={styles.meta}>
                        <span className={styles.owner}>{OWNER_LABELS[app.revenueOwner]}</span>
                        {app.monetization.type !== 'none' && (
                          <span className={styles.monetization}>{app.monetization.type.replace('_', ' ')}</span>
                        )}
                      </div>
                      {app.techStack.length > 0 && (
                        <div className={styles.techStack}>
                          {app.techStack.map((tech) => (
                            <span key={tech} className={styles.tech}>{tech}</span>
                          ))}
                        </div>
                      )}
                      {app.notes && (
                        <div className={styles.notesPreview}>
                          <span className={styles.notesIcon}>📝</span>
                          <span className={styles.notesText}>{app.notes.split('\n')[0]}</span>
                        </div>
                      )}
                      <div className={styles.links} onClick={(e) => e.stopPropagation()}>
                        {app.liveUrl && (
                          <a href={app.liveUrl} target="_blank" rel="noopener noreferrer">Live</a>
                        )}
                        {app.githubRepo && (
                          <a href={`https://github.com/${app.githubRepo}`} target="_blank" rel="noopener noreferrer">GitHub</a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
